import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

/** 32 GUARD_CONFIRM_ACTION_CODE_ATTEMPTS: присланный код сожжён, поможет только новый. */
export const CODE_BURNED = 32
/** 33 GUARD_CODE_TOO_MANY_REQUESTS: запросы/проверки кода закрыты на 15 минут. */
export const CODE_TOO_MANY_REQUESTS = 33
const THROTTLE_MS = 15 * 60 * 1000

export type CodeLimit = 'burned' | 'throttled'

const errorCodeOf = (error: any): unknown => error?.error_code ?? error?.response?.data?.error_code

/**
 * Состояние поля кода после отказа по лимиту: `burned` — поле заблокировано
 * до нового кода, `throttled` — повторная отправка закрыта на 15 минут.
 */
export function useCodeLimit() {
  const { t } = useI18n()
  const limit = ref<CodeLimit | null>(null)
  let timer: ReturnType<typeof setTimeout> | null = null

  const reset = () => {
    if (timer) clearTimeout(timer)
    timer = null
    limit.value = null
  }

  /** Возвращает текст для поля, если ошибка — лимит кода, иначе null. */
  const apply = (error: unknown): string | null => {
    const code = errorCodeOf(error)
    if (code === CODE_BURNED) {
      // Окно 33 важнее: пока оно идёт, новый код всё равно не выдадут.
      if (limit.value !== 'throttled') limit.value = 'burned'
      return t('auth.codeLimits.burned')
    }
    if (code === CODE_TOO_MANY_REQUESTS) {
      reset()
      limit.value = 'throttled'
      timer = setTimeout(reset, THROTTLE_MS)
      return t('auth.codeLimits.tooManyRequests')
    }
    return null
  }

  onBeforeUnmount(reset)

  return {
    limit,
    isBurned: computed(() => limit.value === 'burned'),
    isThrottled: computed(() => limit.value === 'throttled'),
    apply,
    reset,
  }
}
